import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown from "../../../components/Listing/FilterDropdown"
import ExportLogButton from "../../../components/Listing/ExportLogButton"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import { ICONS } from "../../../constants/icons"
import { get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminCityFilterOptions from "../../../hooks/useAdminCityFilterOptions"
import {
  appendProjectCityFilter,
  DEFAULT_PROJECT_SORT_BY,
  DEFAULT_PROJECT_SORT_DIRECTION,
  mapProjectKpi,
  mapProjectRow,
  mapProjectStatusTabs,
  PROJECT_LIST_WITH,
  PROJECT_SORT_OPTIONS,
  PROJECT_STATUS_TABS,
} from "./adminProjectsData"
import { adminProjectPath } from "./adminProjectsPaths"

const PAGE_SIZE = 15

const STATUS_LABEL = {
  present: "Present",
  past: "Past",
  future: "Future",
}

/**
 * Admin Projects listing — GET /api/v1/admin/projects
 * Summary — GET /api/v1/projects/summary
 */
const AdminProjects = () => {
  const navigate = useNavigate()
  const { cityOptions } = useAdminCityFilterOptions()
  const [activeTab, setActiveTab] = useState("all")
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [cityFilter, setCityFilter] = useState("")
  const [sortBy, setSortBy] = useState(DEFAULT_PROJECT_SORT_BY)
  const [sortDirection, setSortDirection] = useState(
    DEFAULT_PROJECT_SORT_DIRECTION,
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [isSummaryLoading, setIsSummaryLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kpi, setKpi] = useState(() => mapProjectKpi())
  const [statusTabs, setStatusTabs] = useState(() => PROJECT_STATUS_TABS)
  const [isExporting, setIsExporting] = useState(false)

  useEffect(() => {
    document.title = "Projects | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearch, cityFilter, sortBy, sortDirection])

  // Clear city if it is no longer in the allowed option set
  // (e.g. city admin options loaded after a stale selection).
  useEffect(() => {
    if (!cityOptions.length || !cityFilter) return
    const allowed = new Set(cityOptions.map(option => String(option.value)))
    if (!allowed.has(String(cityFilter))) setCityFilter("")
  }, [cityFilter, cityOptions])

  const buildSummaryParams = useCallback(() => {
    const params = {}
    appendProjectCityFilter(params, cityFilter)
    return params
  }, [cityFilter])

  const buildListParams = useCallback(
    ({ includePage = true } = {}) => {
      const params = {
        involvement: "all",
        "with[]": PROJECT_LIST_WITH,
        sort_by: sortBy,
        sort_direction: sortDirection,
      }

      if (includePage) {
        params.page = currentPage
        params.page_size = PAGE_SIZE
      }

      if (debouncedSearch) params.search = debouncedSearch
      if (activeTab !== "all") params.status = activeTab
      appendProjectCityFilter(params, cityFilter)

      return params
    },
    [
      activeTab,
      cityFilter,
      currentPage,
      debouncedSearch,
      sortBy,
      sortDirection,
    ],
  )

  const fetchSummary = useCallback(async () => {
    setIsSummaryLoading(true)
    try {
      const response = await get(endpoints.getProjectsSummary, {
        params: buildSummaryParams(),
        skipErrorToast: true,
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        setKpi(mapProjectKpi(result))
        setStatusTabs(mapProjectStatusTabs(result))
      }
    } catch {
      // summary is non-blocking
    } finally {
      setIsSummaryLoading(false)
    }
  }, [buildSummaryParams])

  useEffect(() => {
    fetchSummary()
  }, [fetchSummary])

  const fetchProjects = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await get(endpoints.getAdminProjects, {
        params: buildListParams(),
      })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const mapped = (result.data || []).map(mapProjectRow)
        setRows(mapped)
        setTotalItems(result.total || 0)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil((result.total || 0) / (result.per_page || PAGE_SIZE)),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load projects")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load projects")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [buildListParams, currentPage])

  useEffect(() => {
    fetchProjects()
  }, [fetchProjects])

  const kpiCards = useMemo(
    () => [
      {
        key: "total",
        title: "Total Projects",
        value: kpi.total,
        tone: "lightblue-custom",
      },
      {
        key: "present",
        title: "Active",
        value: kpi.present,
        tone: "lightteal",
      },
      {
        key: "past",
        title: "Past",
        value: kpi.past,
        tone: "red",
      },
      {
        key: "future",
        title: "Future (Scheduled)",
        value: kpi.future,
        tone: "purple-custom",
      },
    ],
    [kpi],
  )

  const handleViewDetail = row => {
    navigate(adminProjectPath(row.id, "overview"))
  }

  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const params = buildListParams({ includePage: false })
      delete params["with[]"]

      const response = await get(endpoints.exportAdminProjects, {
        params,
        responseType: "blob",
        skipErrorToast: true,
      })

      if (!response || response.status >= 400) {
        toastApiError(response, "Failed to export projects")
        return
      }

      const contentType = String(
        response.headers?.["content-type"] || "",
      ).toLowerCase()

      if (contentType.includes("application/json")) {
        const text = await response.data.text()
        let message = "Failed to export projects"
        try {
          const parsed = JSON.parse(text)
          message = parsed?.message || message
        } catch {
          // keep default
        }
        toast.error(message, {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        })
        return
      }

      const disposition = response.headers?.["content-disposition"] || ""
      const filenameMatch = disposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i,
      )
      const filename = filenameMatch?.[1]
        ? filenameMatch[1].replace(/['"]/g, "")
        : `projects-${dayjs().format("YYYY-MM-DD")}.csv`

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)

      toast.success("Projects exported.", {
        position: "bottom-center",
        theme: "colored",
        transition: Bounce,
      })
    } catch (err) {
      toastApiError(err, "Failed to export projects")
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className="admin-page admin-projects">
      <div className="admin-projects__kpi-grid">
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className="admin-projects__kpi-card"
            fullHeight
          >
            <p className="admin-projects__kpi-value">
              {isSummaryLoading ? "…" : card.value}
            </p>
          </DashboardCard>
        ))}
      </div>

      <div className="admin-projects__toolbar">
        <div className="admin-projects__toolbar-left">
          <h1 className="admin-projects__title">Projects</h1>
          <ListingTabs
            items={statusTabs}
            activeKey={activeTab}
            onChange={setActiveTab}
          />
        </div>
        <div className="admin-projects__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by project..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-projects__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "city",
                type: "select",
                label: "City",
                multiple: false,
                searchable: true,
                options: cityOptions,
                value: cityFilter,
                defaultValue: "",
                onChange: next => {
                  const value = Array.isArray(next)
                    ? String(next[0] || "")
                    : String(next || "")
                  setCityFilter(value)
                },
              },
              {
                key: "sortBy",
                type: "sortBy",
                label: "Sort By",
                value: sortBy,
                defaultValue: DEFAULT_PROJECT_SORT_BY,
                onChange: next => {
                  setSortBy(next || DEFAULT_PROJECT_SORT_BY)
                },
                options: PROJECT_SORT_OPTIONS,
              },
              {
                key: "sortDirection",
                type: "sortDirection",
                label: "Order",
                value: sortDirection,
                defaultValue: DEFAULT_PROJECT_SORT_DIRECTION,
                onChange: next => {
                  setSortDirection(next || DEFAULT_PROJECT_SORT_DIRECTION)
                },
              },
            ]}
          />
          <ExportLogButton
            entityLabel="projects"
            onClick={handleExport}
            loading={isExporting}
            disabled={isExporting}
          />
        </div>
      </div>

      <div className="admin-projects__table-wrapper">
        <div
          className="admin-projects__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-projects__table-row admin-projects__table-row--head"
            role="row"
          >
            <div role="columnheader">Project Title</div>
            <div role="columnheader">Category</div>
            <div role="columnheader">City</div>
            <div role="columnheader">Members</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Start Date</div>
            <div role="columnheader" className="admin-projects__actions-head">
              Actions
            </div>
          </div>

          {isLoading
            ? Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={`project-skel-${index}`}
                  className="admin-projects__table-row admin-projects__table-row--skeleton"
                  role="row"
                >
                  <div className="admin-projects__skeleton" />
                  <div className="admin-projects__skeleton" />
                  <div className="admin-projects__skeleton" />
                  <div className="admin-projects__skeleton" />
                  <div className="admin-projects__skeleton" />
                  <div className="admin-projects__skeleton" />
                  <div className="admin-projects__skeleton" />
                </div>
              ))
            : null}

          {!isLoading &&
            rows.map(row => (
              <div
                key={row.id}
                className="admin-projects__table-row"
                role="row"
              >
                <div className="admin-projects__user" role="cell">
                  <div className="admin-projects__avatar" aria-hidden="true">
                    {row.initials}
                  </div>
                  <TruncatedText className="admin-projects__name">
                    {row.title}
                  </TruncatedText>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-projects__cell"
                  role="cell"
                >
                  {row.category}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-projects__cell"
                  role="cell"
                >
                  {row.city}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-projects__cell"
                  role="cell"
                >
                  {row.members}
                </TruncatedText>
                <div className="admin-projects__status-cell" role="cell">
                  <span
                    className={`admin-projects__status admin-projects__status--${row.status}`}
                  >
                    <span
                      className="admin-projects__status-dot"
                      aria-hidden="true"
                    />
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-projects__cell"
                  role="cell"
                >
                  {row.startDate}
                </TruncatedText>
                <div className="admin-projects__actions" role="cell">
                  <CustomButton
                    variant="transparent"
                    size="sm"
                    type="button"
                    trailingIconSrc={ICONS.arrowRight12}
                    trailingIconAlt=""
                    onClick={() => handleViewDetail(row)}
                  >
                    View Detail
                  </CustomButton>
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-projects__empty">
              <EmptyState
                title="No projects found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-projects__empty">
              <EmptyState
                title="Unable to load projects"
                description={error}
                actionText="Try again"
                onActionClick={fetchProjects}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Projects"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-projects__pagination"
      />
    </div>
  )
}

export default AdminProjects
